import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNotify } from 'react-admin';

import type { Receipt } from './receiptApi';
import { fetchReceiptBlob, openReceipt } from './receiptApi';

type Props = {
    receipt: Receipt;
};

export const ReceiptFilePreview = ({ receipt }: Props) => {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [sourceUrl, setSourceUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [mediaReady, setMediaReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;

        setLoading(true);
        setSourceUrl(null);
        setError(null);
        setMediaReady(false);

        void fetchReceiptBlob(receipt.id)
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSourceUrl(objectUrl);
            })
            .catch(err => {
                if (cancelled) return;
                const message =
                    err instanceof Error ? err.message : 'Could not load receipt';
                setError(message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [receipt.id, notify]);

    const contentType = receipt.content_type || 'image/jpeg';
    const isImage = contentType.startsWith('image/');
    const isPdf =
        contentType === 'application/pdf' ||
        receipt.original_filename.endsWith('.pdf');

    const handleImageError = () => {
        // Fallback for unsupported image formats (e.g., HEIC)
        setMediaReady(true);
        setSourceUrl(null);
    };

    if (error) {
        return (
            <Alert severity="error" sx={{ width: '100%' }}>
                {error}
            </Alert>
        );
    }

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    width: '100%',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!sourceUrl) {
        return (
            <Stack spacing={2} sx={{ width: '100%' }}>
                <Typography color="text.secondary">
                    Preview is not available for this file type.
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => void openReceipt(receipt.id)}
                >
                    Open in new tab
                </Button>
            </Stack>
        );
    }

    if (isImage) {
        return (
            <Box
                component="img"
                src={sourceUrl}
                alt={receipt.merchant || receipt.original_filename}
                onLoad={() => setMediaReady(true)}
                onError={handleImageError}
                sx={{
                    maxWidth: '100%',
                    maxHeight: { xs: '50vh', md: '60vh' },
                    objectFit: 'contain',
                    display: mediaReady ? 'block' : 'none',
                    width: '100%',
                }}
            />
        );
    }

    if (isPdf) {
        return (
            <Box
                component="iframe"
                src={sourceUrl}
                title={receipt.merchant || receipt.original_filename}
                onLoad={() => setMediaReady(true)}
                sx={{
                    width: '100%',
                    height: { xs: '50vh', md: '60vh' },
                    border: 0,
                    display: mediaReady ? 'block' : 'none',
                }}
            />
        );
    }

    // Fallback for unsupported types
    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <Typography color="text.secondary">
                Preview is not available for this file type.
            </Typography>
            <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => void openReceipt(receipt.id)}
            >
                Open in new tab
            </Button>
        </Stack>
    );
};

ReceiptFilePreview.displayName = 'ReceiptFilePreview';
